<template>
  <div v-if="rawfile_configured" class="d-flex flex-row flex-wrap">
    <div v-if="rawfile" class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('DIMENSIONS')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-primary"
        />
        <text-fitter
          :value="file_dimensions"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>

    <div v-if="rawfile" class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('SIZE')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-success"
        />
        <text-fitter
          :value="file_size"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>
    <div v-if="rawfile" class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('UPLOAD_DATE')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-warning"
        />
        <text-fitter
          :value="file_upload_date"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>

    <div class="flex-w-stretch">
      <b-card class="border-0" no-body>
        <text-fitter
          :value="$t('ORIGIN')"
          :font="{ family: 'Bitter', size: 16 }"
          class="py-2 pl-2 border-bottom-danger"
        />
        <text-fitter
          :value="file_origin"
          :font="{ family: 'Bitter', size: 32, weight: 'bold' }"
          class="py-4 pl-2 font-weight-bold thumbnail-value"
        />
      </b-card>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "DIMENSIONS": "DIMENSIONS",
    "SIZE": "SIZE",
    "COERCION_DURATION": "COERCION DURATION",
    "UPLOAD_DATE": "UPLOAD DATE",
    "ORIGIN": "ORIGIN",
    "client": "Browser",
    "server": "Server",
    "soon_available": "Available soon",
    "undetermined": "Undetermined"
  },
  "fr": {
    "DIMENSIONS": "DIMENSIONS",
    "SIZE": "TAILLE",
    "COERCION_DURATION": "DUREE DE COERCION",
    "UPLOAD_DATE": "DATE D'UPLOAD",
    "ORIGIN": "ORIGINE",
    "client": "Navigateur",
    "server": "Serveur",
    "soon_available": "Bientôt disponible",
    "undetermined": "Indéterminée"
  }
}

</i18n>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import numeral from 'numeral'

import { mapGetters } from 'vuex'

import { get_columnstore_general_metadata } from '@/api/subscriptions.js'
import { extended_dict_getValue } from '@/utils.js'

import {
  brick_mixin,
  subscription_mixin,
  summary_mixin,
} from '@/components/mixin.js'
import TextFitter from './text_fitter.vue'

// *****************************************************************************

export default {
  name: 'SummaryFile',
  components: { TextFitter },
  mixins: [brick_mixin, subscription_mixin, summary_mixin],
  // props: ['project_id', 'brick_id', 'metavariables_dicts_poids'],
  props: ['project_id', 'brick_id', 'current_metavariables_dict_poid'],
  data() {
    return {
      nb_rows: undefined,
      nb_columns: undefined,
    }
  },
  computed: {
    rawfiles_undeleted() {
      return this.automl_rawfiles_undeleted_object_per_project(this.project_id)
    },
    rawfile_configured() {
      return (
        this.cur_brick.uploaded_rawfile_uid !== -1 ||
        !_.isNull(this.cur_brick.generic_sourcefilepath)
      )
    },
    cur_rawfile_uid() {
      let res = extended_dict_getValue(
        this.cur_brick.metavariables_dict_to_rawfile_uid,
        this.cur_brick.default_metavariables_dict
      )
      if (_.isUndefined(res)) {
        res = -1
      }
      if (this.cur_brick.uploaded_rawfile_uid !== -1) {
        res = this.cur_brick.uploaded_rawfile_uid
      }
      return res
    },
    rawfile() {
      let res = this.rawfiles_undeleted.rawfiles[this.cur_rawfile_uid]
      return res
    },
    file_upload_date() {
      return moment
        .tz((this.rawfile || {}).creation_dt, 'Europe/Paris')
        .format('DD/MM/YY HH[:]mm')
    },
    file_size() {
      let size = (this.rawfile || {}).size
      if (_.isUndefined(size)) {
        size = this.$t('undetermined')
      } else {
        size = numeral(size).format('0.00b')
      }
      return size
    },
    file_dimensions() {
      let res = this.$t('currently_computing')
      if (this.nb_rows && this.nb_columns) {
        res = this.nb_rows + 'L x ' + this.nb_columns + 'C'
      }
      return res
    },
    file_origin() {
      let res = this.$t('currently_computing')
      if (this.rawfile_configured) {
        if (this.cur_rawfile_uid !== -1) {
          let origin_category = (this.rawfile || {}).origin_category
          if (_.isUndefined(origin_category)) {
            res = ''
          } else {
            res = `${this.$t(origin_category)} ${this.rawfile.origin_path}`
          }
        } else {
          let origin_category = 'server'
          let origin_path = this.cur_brick.generic_sourcefilepath.path
          res = `${this.$t(origin_category)} ${origin_path}`
        }
      }
      return res
    },
    coercion_duration() {
      let res = this.$t('soon_available')
      return res
    },
    ...mapGetters(['automl_rawfiles_undeleted_object_per_project']),
  },
  watch: {
    poids_instantiated: {
      immediate: true,
      async handler(new_val) {
        if (new_val) {
          let res = await get_columnstore_general_metadata(
            this.project_id,
            this.current_metavariables_dict_poid
          )
          this.nb_columns = (res.pipe_object || {}).nb_cols
          this.nb_rows = (res.pipe_object || {}).nb_rows
        }
      },
    },
  },
  beforeMount() {
    this.fetch_collections(['rawfiles'])
  },
}
</script>
